import { React, useRef, useContext, useMemo } from 'react';
import CustomSpinner from '../UI/Spinner/Spinner';
import CustomModal from '../UI/Modal/Modal';
import './TestExecutions.css';
import TestExecutionsTable from './TestExecutionsTable';
import EditNameModal from './EditNameModal/EditNameModal';
import { isATC } from '../../Utils/helpers';
import { AppContext } from '../UI/contexts';
import ScheduleButtons from './Schedule/ScheduleButtons';

import { useTestExecutionState } from '../../Hooks/TestExecutions/useTestExecutionState';
import { useTestExecutionMutations } from '../../Hooks/TestExecutions/useTestExecutionMutations';
import { useTestExecutionFavorites } from '../../Hooks/TestExecutions/useTestExecutionFavorites';
import { useTestExecutionActions } from '../../Hooks/TestExecutions/useTestExecutionActions';
import { useTestExecutionData } from '../../Hooks/TestExecutions/useTestExecutionData';

function TestExecutions({
  data,
  isScheduled,
  refetch,
  refetchVariables,
  perPage,
  itemCount,
  deleteTests,
  cancelExecution,
  getTestResultsUrl,
  editRoute,
  type,
  rerunTestExecutionMutation,
  getRerunTestExecutionVariables,
  filterVariables,
}) {
  // Get auth context
  const { user } = useContext(AppContext);
  const roles = user.roles.map((role) => role.name);
  const isCheckVisible = useRef(
    isScheduled && (roles.includes('admin') || roles.includes('developer'))
  );

  // STATE
  const {
    testExecutions,
    setTestExecutions,
    showFavorites,
    setShowFavorites,
    showScheduled,
    setShowScheduled,
    selectedIds,
    setSelectedIds,
    selectedToCancelIds,
    setSelectedToCancel,
    selectedToSchedule,
    setSelectedtoSchedule,
    showModal,
    setShowModal,
    showCancelModal,
    setShowCancelModal,
    showSingleDeleteModal,
    setShowSingleDeleteModal,
    showEditModal,
    setShowEditModal,
    selectedToRerunTest,
  } = useTestExecutionState();

  // MUTATIONS
  const {
    runEditedMutation,
    removeFavorite,
    addFavorite,
    stopResumeScheduledExecution,
    rerunTestExecution,
  } = useTestExecutionMutations(rerunTestExecutionMutation);

  // HANDLERS
  const { addToFavoritesHandler, removeFromFavoritesHandler } =
    useTestExecutionFavorites(addFavorite, removeFavorite, user);

  // ACTIONS
  const {
    onCancelTestExecution,
    handleMultiSelectFilter,
    deleteSelectedHandler,
    deleteSingleHandler,
    rerunAcceptHandler,
    seeTestResultsHandler,
    deleteSingleClickHandler,
    rerunSingleClickHandler,
    scheduleSingleClickHandler,
    cancelExecutionHandler,
  } = useTestExecutionActions({
    cancelExecution,
    deleteTests,
    refetch,
    selectedToCancelIds,
    setShowCancelModal,
    selectedIds,
    setShowModal,
    setSelectedIds,
    setShowSingleDeleteModal,
    rerunTestExecution,
    getRerunTestExecutionVariables,
    selectedToRerunTest,
    setShowEditModal,
    setShowFavorites,
    setShowScheduled,
    getTestResultsUrl,
    runEditedMutation,
    setSelectedToCancel,
  });

  const { onStopResumeScheduledExecution } = useTestExecutionData({
    data,
    user,
    showFavorites,
    showScheduled,
    testExecutions,
    setTestExecutions,
    stopResumeScheduledExecution,
    selectedToSchedule,
  });

  const testExecutionsTable = useMemo(() => {
    return (
      <TestExecutionsTable
        data={testExecutions}
        isScheduled={isCheckVisible.current}
        perPage={perPage}
        refetchVariables={refetchVariables}
        itemCount={itemCount}
        handleMultiSelectFilter={handleMultiSelectFilter}
        isATCTable={isATC(type)}
        seeTestResultsHandler={seeTestResultsHandler}
        deleteSingleClickHandler={deleteSingleClickHandler}
        rerunSingleClickHandler={rerunSingleClickHandler}
        scheduleSingleClickHandler={scheduleSingleClickHandler}
        addToFavoritesHandler={addToFavoritesHandler}
        removeFromFavoritesHandler={removeFromFavoritesHandler}
        cancelExecutionHandler={cancelExecutionHandler}
        editRoute={editRoute}
        refetch={refetch}
        filterVariables={filterVariables}
        setSelectedToSchedule={setSelectedtoSchedule}
      />
    );
  }, [testExecutions]);

  return data ? (
    <div className='test-execution-container'>
      {isCheckVisible.current && (
        <div className='schedule-buttons-container'>
          <ScheduleButtons
            selectedToSchedule={selectedToSchedule}
            onStopResumeScheduledExecution={onStopResumeScheduledExecution}
          />
        </div>
      )}
      {testExecutionsTable}
      <div id='test-executions-modals'>
        <EditNameModal
          show={showEditModal}
          initialValue={selectedToRerunTest.current?.name}
          title='Set the name for the new execution'
          onHide={() => setShowEditModal(false)}
          onCancel={() => setShowEditModal(false)}
          onAccept={(newName) => rerunAcceptHandler(newName)}
        />
        <CustomModal
          show={showSingleDeleteModal}
          title='Are you sure you want to delete this Test execution?'
          onClose={() => setShowSingleDeleteModal(false)}
          onAccept={() => deleteSingleHandler()}
        />
        <CustomModal
          show={showCancelModal}
          title='Are you sure you want to cancel this execution?'
          onClose={() => setShowCancelModal(false)}
          onAccept={() => onCancelTestExecution()}
        />
        <CustomModal
          show={showModal}
          title='Are you sure you want to delete these Test executions?'
          onClose={() => setShowModal(false)}
          onAccept={() => deleteSelectedHandler()}
        />
      </div>
    </div>
  ) : (
    <CustomSpinner />
  );
}

export default TestExecutions;
